<template>
  <footer class="bg-gray-900 dark:bg-gray-950 text-gray-300 dark:text-gray-400 transition-colors duration-200">
    <div class="container mx-auto px-4 py-12">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- Company Info -->
        <div>
          <div class="flex items-center space-x-3 mb-4">
            <img 
              :src="isDark ? logoDark : logoLight"
              alt="Walabi"
              class="w-8 h-8"
            >
            <span class="text-xl font-bold text-white">Walabi</span>
          </div>
          <p class="text-sm leading-relaxed">
            Tu tienda online de confianza con los mejores productos al mejor precio. 
            Calidad garantizada y envío rápido a todo el país.
          </p>
          <div class="flex space-x-4 mt-4">
            <a href="https://www.instagram.com/walabi_py/" target="_blank" rel="noopener noreferrer" class="text-gray-400 hover:text-primary-400 transition-colors" title="Síguenos en Instagram">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/WalabPy" target="_blank" rel="noopener noreferrer" class="text-gray-400 hover:text-primary-400 transition-colors" title="Síguenos en Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
  <path d="M22.675 0h-21.35C.595 0 0 .594 0 1.326v21.348C0 23.405.595 24 1.325 24H12.82v-9.294H9.692v-3.622h3.127V8.413c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.464.099 2.796.143v3.24l-1.918.001c-1.504 0-1.796.715-1.796 1.763v2.313h3.588l-.467 3.622h-3.121V24h6.116c.73 0 1.324-.595 1.324-1.326V1.326C24 .594 23.405 0 22.675 0z"/>
</svg>
            </a>
            <a href="https://www.tiktok.com/@walabipy" target="_blank" rel="noopener noreferrer" class="text-gray-400 hover:text-primary-400 transition-colors" title="Síguenos en TikTok">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Quick Links - Categories -->
        <div>
          <h3 class="font-semibold text-white mb-4">Enlaces Rápidos</h3>
          <ul class="space-y-2 text-sm">
            <li v-for="category in lastFiveCategories" :key="category.id">
              <router-link 
                :to="{ name: 'Products', query: { category: category.id } }" 
                class="hover:text-primary-400 transition-colors"
              >
                {{ category.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Customer Service -->
        <div>
          <h3 class="font-semibold text-white mb-4">Atención al Cliente</h3>
          <ul class="space-y-2 text-sm">
            <li>
              <router-link
                to="/help-center/"
                class="hover:text-primary-400 transition-colors"
              >
                Centro de Ayuda
              </router-link>
            </li>
            <li>
              <router-link to="/returns-policy" class="hover:text-primary-400 transition-colors">
                Política de Devoluciones
              </router-link>
            </li>
            <li>
              <router-link to="/shipping-and-delivery" class="hover:text-primary-400 transition-colors">
                Envíos y Entregas
              </router-link>
            </li>
            <li>
              <router-link to="/terms-and-conditions" class="hover:text-primary-400 transition-colors">
                Términos y Condiciones
              </router-link>
            </li>
            <li>
              <router-link to="/privacy-policy" class="hover:text-primary-400 transition-colors">
                Política de Privacidad
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div>
          <h3 class="font-semibold text-white mb-4">Contacto</h3>
          <div class="space-y-3 text-sm">
            <div class="flex items-center space-x-3">
              <PhoneIcon class="w-4 h-4 text-primary-400 flex-shrink-0" />
              <span>+595 986 333 184</span>
            </div>
            <div class="flex items-center space-x-3">
              <EnvelopeIcon class="w-4 h-4 text-primary-400 flex-shrink-0" />
              <span>soporte@walabi.com.py</span>
            </div>
            <div class="flex items-start space-x-3">
              <MapPinIcon class="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
              <span>Maria Auxiliadora esq Agustin Lara<br>San Lorenzo, Departamento Central - Paraguay</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="border-t border-gray-800 dark:border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
        <p class="text-sm text-gray-400">
          {{ currentYear }} Walabi. Todos los derechos reservados.
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useProductsStore } from '@/stores/products';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/vue/24/outline';
import logoLight from '@/assets/logo_mini_walabi.png';
import logoDark from '@/assets/logo_mini_walabi_dark.png';

const productsStore = useProductsStore();

// Detect dark mode
const isDark = computed(() => {
  return document.documentElement.classList.contains('dark');
});

// Get the last 5 categories
const lastFiveCategories = computed(() => {
  return productsStore.categories.slice(-5);
});

const currentYear = new Date().getFullYear();

onMounted(async () => {
  // Load categories if not already loaded
  if (productsStore.categories.length === 0) {
    await productsStore.fetchCategories();
  }
});
</script>
